import { useEffect, useRef, useState } from 'react'

import api from '@disco/api/server'
import { TRACK_LOAD_BATCH } from '@disco/constants/ActionTypes'
import { TRACK } from '@disco/constants/ItemTypes'

import type { ClientLibraryDiscoveryPanelItem } from 'types/client-library'
import type { Track } from 'types/track'

import { useDispatch } from 'hooks/use-redux'

const MAIN_PAGE_TRACKS_COUNT = 5

export const useGetClientLibraryCatalogTracks = (
  catalog: ClientLibraryDiscoveryPanelItem | null,
) => {
  const dispatch = useDispatch()
  const [catalogTracks, setCatalogTracks] = useState<Record<string, Track[]>>(
    {},
  )
  const [isLoading, setIsLoading] = useState(!!catalog)
  const [error, setError] = useState<string | null>(null)
  const fetchIdRef = useRef(0)
  const catalogId = catalog?.id
  const catalogPublicUrl = catalog?.public_url

  useEffect(() => {
    if (!catalogId) {
      setIsLoading(false)
      return
    }

    let active = true
    setIsLoading(true)
    setError(null)

    const currentFetchId = ++fetchIdRef.current

    const loadTracks = async () => {
      try {
        const result = await api.getClientLibraryDiscoveryPanelById(
          catalogId,
          MAIN_PAGE_TRACKS_COUNT,
        )

        if (!active || currentFetchId !== fetchIdRef.current) return

        const tracks = result?.featured_tracks ?? []

        // Format tracks with proper type and dispatch batch load to Redux
        // This is required for the player to find and play the tracks
        // Add client_library_url for waveform loading if track doesn't have one
        const tracksWithType = tracks.map(track => ({
          ...track,
          type: TRACK,
          groups: track.groups ?? [],
          client_library_url: track.client_library_url || catalogPublicUrl,
        }))

        dispatch({
          type: TRACK_LOAD_BATCH,
          list: tracksWithType,
          resetPlaylistInfo: false,
        })

        // Always set tracks, even if empty, to indicate fetch completed
        setCatalogTracks({ [catalogId]: tracks })
      } catch (err) {
        if (!active || currentFetchId !== fetchIdRef.current) return
        console.error('Error loading featured tracks:', err)
        setError('Failed to load tracks.')
      } finally {
        if (active && currentFetchId === fetchIdRef.current) {
          setIsLoading(false)
        }
      }
    }

    loadTracks()

    return () => {
      active = false
    }
  }, [catalogId, catalogPublicUrl, dispatch])

  return { catalogTracks, isLoading, error }
}
